import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getOrder } from '../utils/api';

const OrderStatus = () => {
  const [invoiceId, setInvoiceId] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!invoiceId.trim()) return;

    setLoading(true);
    setError(null);
    setOrder(null);

    try {
      const result = await getOrder(invoiceId.trim());
      setOrder(result);
    } catch (err) {
      setError(err.message || 'Order not found');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const capitalizeStatus = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusDisplay = (order) => {
    if (order.boostStatus === 'completed') {
      return { label: 'Boosts Processed', className: 'completed' };
    }
    if (order.boostStatus === 'processing') {
      return { label: 'Processing', className: 'pending' };
    }
    if (order.status === 'confirmed') {
      return { label: 'Confirmed', className: 'completed' };
    }
    if (order.status === 'confirming') {
      return { label: 'Confirming', className: 'pending' };
    }
    if (order.status === 'waiting') {
      return { label: 'Waiting', className: 'pending' };
    }
    if (order.status === 'sending') {
      return { label: 'Sending', className: 'pending' };
    }
    if (order.status === 'expired') {
      return { label: 'Expired', className: 'expired' };
    }
    return { label: capitalizeStatus(order.status), className: 'pending' };
  };

  return (
    <main className="main-content">
      <div className="order-status-page">
        <div className="search-box">
          <h1>Order Status</h1>
          <p>Enter your invoice ID to check your order status</p>
          <div className="search-row">
            <input
              type="text"
              className="form-input"
              placeholder="Enter your invoice ID"
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button 
              className="btn btn-primary"
              onClick={handleSearch}
              disabled={loading || !invoiceId.trim()}
            >
              {loading ? (
                <span className="loading-spinner"></span>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  Search
                </>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="order-details" style={{ textAlign: 'center' }}>
            <p style={{ color: 'var(--red-primary)', marginBottom: '1rem' }}>{error}</p>
            <Link to="/" className="btn btn-primary" style={{ display: 'inline-flex', width: 'auto' }}>
              Create New Order
            </Link>
          </div>
        )}

        {order && order.status === 'expired' && (
          <div className="order-details">
            <div className="expired-modal">
              <div className="expired-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="15" y1="9" x2="9" y2="15"/>
                  <line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
              </div>
              <h3>Payment Expired</h3>
              <p>Order has expired. Please create a new order.</p>
            </div>
            <button 
              className="btn btn-primary" 
              style={{ marginTop: '1rem' }}
              onClick={() => window.location.href = '/'}
            >
              Create New Order
            </button>
          </div>
        )}

        {order && order.status !== 'expired' && (
          <div className="order-details">
            <h2>Order Details</h2>
            <div className="detail-row">
              <span className="detail-label">Order ID</span>
              <span className="detail-value" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {order.id}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status</span>
              <span className="detail-value">
                <span className={`status-badge ${getStatusDisplay(order).className}`}>
                  {getStatusDisplay(order).label}
                </span>
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Product</span>
              <span className="detail-value">
              {order.billingType === 'annual'
                ? `Annual ~ ${
                    (order.annualMonths || 12) === 12
                      ? '1 Year'
                      : `${order.annualMonths} Months`
                  }`
                : `${order.productName}`
              }
            </span>

            </div>
            <div className="detail-row">
              <span className="detail-label">Quantity</span>
              <span className="detail-value">{order.quantity} Boosts</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Payment Method</span>
              <span className="detail-value">{order.cryptoType?.replace('_', ' ') || 'Crypto'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Total</span>
              <span className="detail-value" style={{ color: 'var(--green-primary)', fontWeight: 700 }}>
                ${order.total?.toFixed(2)}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Created</span>
              <span className="detail-value">{formatDate(order.createdAt)}</span>
            </div>
            {['waiting', 'confirming'].includes(order.status) && (
              <div className="detail-row">
                <span className="detail-label">Expires</span>
                <span className="detail-value">{formatDate(order.expiresAt)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
};

export default OrderStatus;
